import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Switch,
  FormControlLabel,
  Button,
  Grid,
  Alert,
  Snackbar,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Save, Refresh } from '@mui/icons-material';
import { getGroupBuySettings, updateGroupBuySettings } from '../../services/groupBuyService';

const Settings = () => {
  const { t } = useTranslation();
  const [settings, setSettings] = useState({
    auto_close_expired_pools: true,
    default_pool_expiration_days: 7,
    max_pool_expiration_days: 30,
    require_admin_approval: false,
    min_target_quantity: 2,
    max_participants_default: 50,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await getGroupBuySettings();
      if (response.success) {
        setSettings(response.settings);
      }
    } catch (error) {
      showSnackbar('Failed to load settings', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const response = await updateGroupBuySettings(settings);
      if (response.success) {
        showSnackbar('Settings saved successfully');
      } else {
        showSnackbar(response.message || 'Failed to save settings', 'error');
      }
    } catch (error) {
      showSnackbar('Failed to save settings', 'error');
    } finally {
      setSaving(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      backgroundColor: 'background.default',
      px: { xs: 0.75, sm: 1, md: 1.5 },
      py: { xs: 2, sm: 2.5, md: 3 },
      maxWidth: '95vw',
      overflowX: 'hidden'
    }}>
      {/* Header */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 3,
        flexWrap: 'wrap',
        gap: 2
      }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#DC143C' }}>
          Group Buy Settings
        </Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={loadSettings}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<Save />}
            onClick={handleSave}
            disabled={saving || loading}
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </Box>
      </Box>

      {loading ? (
        <Typography>Loading settings...</Typography>
      ) : (
        <Grid container spacing={3}>
          {/* Pool Management Settings */}
          <Grid size={{ xs: 12}}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Pool Management
                </Typography>
                <Typography variant="body2" color="textSecondary" mb={3}>
                  Configure how group buy pools are managed and moderated
                </Typography>

                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.require_admin_approval}
                          onChange={(e) => updateSetting('require_admin_approval', e.target.checked)}
                          color="primary"
                        />
                      }
                      label="Require Admin Approval"
                      labelPlacement="start"
                      sx={{ ml: 0, justifyContent: 'space-between' }}
                    />
                    <Typography variant="caption" color="textSecondary">
                      Pools must be approved by admin before becoming active
                    </Typography>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.auto_close_expired_pools}
                          onChange={(e) => updateSetting('auto_close_expired_pools', e.target.checked)}
                          color="primary"
                        />
                      }
                      label="Auto-close Expired Pools"
                      labelPlacement="start"
                      sx={{ ml: 0, justifyContent: 'space-between' }}
                    />
                    <Typography variant="caption" color="textSecondary">
                      Automatically close pools when they expire
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Time Settings */}
          <Grid size={{ xs: 12 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Time Settings
                </Typography>
                <Typography variant="body2" color="textSecondary" mb={3}>
                  Configure default expiration times and limits
                </Typography>

                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Default Expiration (Days)"
                      type="number"
                      value={settings.default_pool_expiration_days}
                      onChange={(e) => updateSetting('default_pool_expiration_days', parseInt(e.target.value) || 7)}
                      inputProps={{ min: 1, max: 365 }}
                      helperText="Default number of days until pools expire"
                    />
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Maximum Expiration (Days)"
                      type="number"
                      value={settings.max_pool_expiration_days}
                      onChange={(e) => updateSetting('max_pool_expiration_days', parseInt(e.target.value) || 30)}
                      inputProps={{ min: 1, max: 365 }}
                      helperText="Maximum allowed expiration days for pools"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Quantity Settings */}
          <Grid size={{ xs: 12 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Quantity Settings
                </Typography>
                <Typography variant="body2" color="textSecondary" mb={3}>
                  Configure minimum and maximum quantities for pools
                </Typography>

                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Minimum Target Quantity"
                      type="number"
                      value={settings.min_target_quantity}
                      onChange={(e) => updateSetting('min_target_quantity', parseInt(e.target.value) || 2)}
                      inputProps={{ min: 2, max: 1000 }}
                      helperText="Minimum quantity required for group discounts"
                    />
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Default Max Participants"
                      type="number"
                      value={settings.max_participants_default}
                      onChange={(e) => updateSetting('max_participants_default', parseInt(e.target.value) || 50)}
                      inputProps={{ min: 5, max: 1000 }}
                      helperText="Default maximum number of participants per pool"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* System Information */}
          <Grid size={{ xs: 12 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  System Information
                </Typography>
                <Alert severity="info" sx={{ mb: 2 }}>
                  These settings affect how the group buying system operates across the platform.
                  Changes take effect immediately and may affect existing pools.
                </Alert>

                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="textSecondary">
                    <strong>Note:</strong> Admin approval is currently disabled as per system requirements.
                    Pools become active immediately upon creation without requiring admin review.
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Settings;